import * as React from 'react';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import type { Props as CMLEditorProps } from 'bundles/authoring/content-authoring/components/CMLEditor';
import CMLEditorOrFallbackEditor from 'bundles/authoring/content-authoring/components/CMLEditorOrFallbackEditor';
import type { CmlContent, ImageUploadOptions } from 'bundles/cml';
import type CourseRoles from 'bundles/common/constants/CourseRoles';
import { getCustomTools as getToolbarItemsForRole } from 'bundles/discussions/utils/DiscussionsCMLUtils';
import CourseMembershipStore from 'bundles/ondemand/stores/CourseMembershipStore';

import _t from 'i18n!nls/discussions';

type CourseRole = keyof typeof CourseRoles;

type Props = {
  cml: CmlContent;
  onChange: (cml: CmlContent) => void;
  imageUploadOptions?: ImageUploadOptions;
  placeholder?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  onFirstFocus?: () => void;
  removeFocus?: () => void;
  ariaInvalid?: boolean;
} & Partial<CMLEditorProps>;

/**
 * Wrapper around CMLEditor for all forumsV2 CML editing instances such as replying and creating new threads/posts.
 */
const ForumsCmlEditor = ({
  cml,
  onChange,
  imageUploadOptions,
  placeholder,
  onFocus,
  onBlur,
  ariaLabel,
  ariaDescribedBy,
  focusOnLoad = true,
  ariaRequired,
  ariaInvalid,
  ...restProps
}: Props) => {
  const { courseRole } = useFluxibleStore(CourseMembershipStore, (store) => ({
    courseRole: store.getCourseRole() as CourseRole | undefined,
  }));

  const toolbarItems = React.useMemo(() => getToolbarItemsForRole(courseRole), [courseRole]);

  return (
    <div className="rc-ForumsCmlEditor">
      <CMLEditorOrFallbackEditor
        {...restProps}
        initialCML={cml}
        onContentChange={onChange}
        uploadOptions={{ image: imageUploadOptions, asset: imageUploadOptions }}
        isLearnerUpload={true}
        onFocus={onFocus}
        onBlur={onBlur}
        focusOnLoad={focusOnLoad}
        placeholder={placeholder || _t('Reply')}
        customTools={toolbarItems}
        ariaLabel={ariaLabel || _t('Post Reply')}
        ariaDescribedBy={ariaDescribedBy}
        ariaRequired={ariaRequired}
        ariaInvalid={ariaInvalid}
      />
    </div>
  );
};

export default ForumsCmlEditor;
